import { slugBelongsTo } from '@/components/rewards/utils/rewardTypeRegistry'
import { isMerchantRewardProductSkuSlug, parseRewardExternalIdCsv } from '@/components/rewards/utils/rewardHelpers'
import { switchOfferDiscountRewardTypeForScope } from '@/utils/offerHelpers'
import { createManualProduct, getProductKey } from '@/utils/productHelpers'

import type { LoyaltyReward, PartnerProduct, RewardConditionOperator } from '@/types/types'
import type { AccessCondition, UsageLimit } from '@/types/offer'
import type {
  RequiredProductsConditionWire,
  RewardConditionPayload
} from '@/components/rewards/utils/offerRewardPayload'

export const DAYS_PER_WEEK = 7
export const DAYS_PER_MONTH = 30

export interface UsageLimitResult {
  usageLimit: UsageLimit
  frequencyUnit: number
  frequencyTimes: number
}

export interface ClaimLimitValues {
  claimLimitQuantity: number | null
  claimLimitDays: number | null
}

export const parseUsageLimit = (values: ClaimLimitValues): UsageLimitResult => {
  const claimLimitQuantity = values.claimLimitQuantity ?? 0
  const claimLimitDays = values.claimLimitDays ?? 0

  if (claimLimitQuantity > 0 && !claimLimitDays) {
    return { usageLimit: 'quantity', frequencyUnit: 1, frequencyTimes: 1 }
  }

  if (claimLimitQuantity > 0 && claimLimitDays > 0) {
    const periodDays = claimLimitDays > 0 ? claimLimitDays : 1
    let frequencyUnit = 1
    let frequencyTimes = Math.max(1, periodDays)

    if (periodDays % DAYS_PER_WEEK === 0 && periodDays >= DAYS_PER_WEEK) {
      frequencyUnit = DAYS_PER_WEEK
      frequencyTimes = Math.max(1, periodDays / DAYS_PER_WEEK)
    } else if (periodDays % DAYS_PER_MONTH === 0 && periodDays >= DAYS_PER_MONTH) {
      frequencyUnit = DAYS_PER_MONTH
      frequencyTimes = Math.max(1, periodDays / DAYS_PER_MONTH)
    }

    return { usageLimit: 'unlimited', frequencyUnit, frequencyTimes }
  }

  return { usageLimit: 'none', frequencyUnit: 1, frequencyTimes: 1 }
}

export interface ClaimLimitSummaryItem {
  text: string
  icon: 'IconoirClock'
}

export const getClaimLimitSummary = (
  values: ClaimLimitValues,
  t: (key: string, params?: Record<string, string | number>) => string
): ClaimLimitSummaryItem | null => {
  const quantity = values.claimLimitQuantity ?? 0
  if (quantity === 0) return null

  const days = values.claimLimitDays ?? 0
  const prefix = 'pages.offer.create.summary.detail'

  if (days > 0) {
    const usageKey =
      quantity > 1 && days > 1
        ? 'usageLimitedWithPeriodManyMany'
        : quantity > 1 && days === 1
          ? 'usageLimitedWithPeriodManyOne'
          : quantity === 1 && days > 1
            ? 'usageLimitedWithPeriodOneMany'
            : 'usageLimitedWithPeriodOneOne'

    return { text: t(`${prefix}.${usageKey}`, { count: quantity, days }), icon: 'IconoirClock' }
  }

  const usageKey = quantity > 1 ? 'usageLimitedOther' : 'usageLimitedOne'
  return { text: t(`${prefix}.${usageKey}`, { count: quantity }), icon: 'IconoirClock' }
}

export const hydrateItemsFromTargets = (reward: LoyaltyReward): void => {
  if (reward.items && reward.items.length > 0) return

  const rawTargets = reward.targets
  const targetSkusFromTargets: string[] = Array.isArray(rawTargets)
    ? rawTargets.filter((t) => t && typeof t.sku === 'string' && t.sku.length > 0).map((t) => String(t.sku))
    : []

  const rawTargetSkus = reward.targetSkus ?? reward.target_skus
  const targetSkusFromLegacy: string[] = Array.isArray(rawTargetSkus)
    ? rawTargetSkus.filter((sku): sku is string => typeof sku === 'string' && sku.length > 0)
    : []

  const allTargetSkus = [...targetSkusFromTargets, ...targetSkusFromLegacy]

  if (allTargetSkus.length > 0) {
    reward.items = allTargetSkus.map((sku) => createManualProduct(sku))
  }
}

export const hydrateItemsFromExternalId = (reward: LoyaltyReward): void => {
  if (
    !isMerchantRewardProductSkuSlug(reward.rewardType?.slug) ||
    typeof reward.externalId !== 'string' ||
    reward.externalId.trim().length === 0
  ) {
    return
  }

  const skus = parseRewardExternalIdCsv(reward.externalId)
  if (skus.length === 0) return

  const existing = new Set((reward.items ?? []).map((item) => String(item?.externalId ?? '').trim()).filter(Boolean))
  const missing = skus.filter((sku) => !existing.has(sku))
  if (missing.length === 0) return

  reward.items = [...(reward.items ?? []), ...missing.map((sku) => createManualProduct(sku))]

  if (slugBelongsTo('productScopedDiscount', reward.rewardType?.slug) && reward.applyReductionOn !== 'products') {
    reward.applyReductionOn = 'products'
  }
}

export const syncDiscountScope = (reward: LoyaltyReward, rewardTypeIdsBySlug: Record<string, number> | null): void => {
  if (slugBelongsTo('productScopedDiscount', reward.rewardType?.slug) && reward.applyReductionOn === 'cart') {
    switchOfferDiscountRewardTypeForScope(reward, 'cart', rewardTypeIdsBySlug)
  } else if (slugBelongsTo('discount', reward.rewardType?.slug) && reward.applyReductionOn === 'products') {
    switchOfferDiscountRewardTypeForScope(reward, 'products', rewardTypeIdsBySlug)
  }
}

export const hydrateXForYValues = (reward: LoyaltyReward, conditions: RewardConditionPayload[]): void => {
  const xForYCondition = conditions.find((c) => c.type === 'required_products') as
    | RequiredProductsConditionWire
    | undefined
  const initialApiValue = reward.value

  const productsPayload = xForYCondition?.products
  const rawArray = Array.isArray(productsPayload)
    ? productsPayload.map((product) => ({
        external_id: typeof product.externalId === 'string' ? product.externalId : (product.external_id ?? ''),
        quantity: Number(product.quantity ?? 1)
      }))
    : (productsPayload?.items ?? [])
  const requiredProducts = rawArray
    .map((p) => {
      const externalId = (p.external_id ?? '').trim()
      const qty = Number(p.quantity)
      if (!externalId || !Number.isFinite(qty) || qty <= 0) return null
      return { externalId, quantity: Math.trunc(qty) }
    })
    .filter((x): x is { externalId: string; quantity: number } => x != null)

  if (requiredProducts.length > 0) {
    reward.value = requiredProducts[0].quantity
    reward.items = requiredProducts.map((p) => createManualProduct(p.externalId))
  }

  const rawTargets = reward.targets
  if (Array.isArray(rawTargets) && rawTargets.length > 0) {
    const freeQty = rawTargets[0].quantity
    if (typeof freeQty === 'number' && freeQty > 0) {
      reward.voucherDiscountAmount = Math.trunc(freeQty)
    }
  } else {
    const rawFreeQty = reward.voucherDiscountAmount ?? initialApiValue
    const parsedFreeQty = rawFreeQty == null ? NaN : Number(rawFreeQty)
    if (Number.isFinite(parsedFreeQty) && parsedFreeQty > 0) {
      reward.voucherDiscountAmount = Math.trunc(parsedFreeQty)
    }
  }
}

export const normalizeDiscountValues = (reward: LoyaltyReward): void => {
  if (slugBelongsTo('percentDiscount', reward.rewardType?.slug)) {
    const raw = reward.value ?? reward.discountValuePercent ?? null
    const num = raw == null ? NaN : Number(raw)
    const normalized = Number.isFinite(num) ? Math.trunc(num) : null
    reward.value = normalized
    reward.discountValuePercent = normalized
  } else if (slugBelongsTo('discount', reward.rewardType?.slug)) {
    const raw = reward.voucherDiscountAmount ?? reward.value ?? null
    const num = raw == null ? NaN : Number(raw)
    const normalized = Number.isFinite(num) ? num : null
    reward.value = normalized
    reward.voucherDiscountAmount = normalized
  }
}

export interface ParsedConditions {
  cartItemCount: number | null
  requiredProducts: PartnerProduct[]
  requiredProductsOperator: RewardConditionOperator
  requiredProductQuantities: Record<string, number>
  accessCondition: AccessCondition | null
}

export const parseConditions = (conditions: RewardConditionPayload[]): ParsedConditions => {
  let cartItemCount: number | null = null
  let cartMinimumInCents: number | null = null
  let requiredProducts: PartnerProduct[] = []
  let requiredProductsOperator: RewardConditionOperator = 'contains_all'
  const requiredProductQuantities: Record<string, number> = {}

  conditions.forEach((condition) => {
    if (condition.type === 'cart_minimum_in_cents' && typeof condition.value === 'number' && condition.value > 0) {
      cartMinimumInCents = condition.value
    }

    if (condition.type === 'cart_item_count' && typeof condition.value === 'number' && condition.value > 0) {
      cartItemCount = condition.value
    }

    if (condition.type === 'required_products') {
      requiredProductsOperator = condition.operator === 'contains_any' ? 'contains_any' : 'contains_all'

      const wireCondition = condition as RequiredProductsConditionWire
      const rawProducts = wireCondition.products
      const flatProducts = Array.isArray(rawProducts) ? rawProducts : []
      const itemsArray = Array.isArray(rawProducts) ? [] : (rawProducts?.items ?? [])

      if (itemsArray.length > 0 || flatProducts.length > 0) {
        const normalizedFromItems = itemsArray
          .map((p) => {
            const externalId =
              typeof p.externalId === 'string'
                ? p.externalId.trim()
                : typeof p.external_id === 'string'
                  ? p.external_id.trim()
                  : ''
            if (!externalId) return null

            const qty = Number(p.quantity ?? 1)
            const quantity = Number.isFinite(qty) && qty > 0 ? Math.trunc(qty) : 1

            return {
              ...createManualProduct(externalId),
              quantity
            }
          })
          .filter((p): p is PartnerProduct & { quantity: number } => !!p)

        const normalizedFromFlat = flatProducts
          .map((p) => {
            const externalId =
              typeof p.externalId === 'string'
                ? p.externalId.trim()
                : typeof p.external_id === 'string'
                  ? p.external_id.trim()
                  : ''
            if (!externalId) return null

            const qty = Number(p.quantity ?? 1)
            const quantity = Number.isFinite(qty) && qty > 0 ? Math.trunc(qty) : 1

            return {
              ...createManualProduct(externalId),
              quantity
            }
          })
          .filter((p): p is PartnerProduct & { quantity: number } => !!p)
        const normalized = [...normalizedFromItems, ...normalizedFromFlat]

        if (normalized.length > 0) {
          requiredProducts = normalized.map(({ quantity: _q, ...rest }) => rest)
          for (const p of normalized) {
            const key = getProductKey(p)
            requiredProductQuantities[key] = p.quantity
          }
        }
      }
    }
  })

  let accessCondition: AccessCondition | null = null
  if (requiredProducts.length > 0) {
    accessCondition = 'products_required'
  } else if (cartItemCount != null && cartItemCount > 0) {
    accessCondition = 'cart_item_count'
  } else if (cartMinimumInCents != null && cartMinimumInCents > 0) {
    accessCondition = 'min_amount'
  }

  return {
    cartItemCount,
    requiredProducts,
    requiredProductsOperator,
    requiredProductQuantities,
    accessCondition
  }
}

export const parseRequiredProducts = (reward: LoyaltyReward): PartnerProduct[] => {
  const requiredProductIds = reward.requiredProductExternalIds ?? []
  if (requiredProductIds.length === 0) return []

  return requiredProductIds.map((id: string) => {
    const existingItem = reward.items?.find((p) => p.externalId.toString() === id)
    if (existingItem) {
      return { ...existingItem, partnerId: existingItem.partnerId ?? 0 }
    }
    return createManualProduct(id)
  })
}
